import { observer } from 'mobx-react-lite'
import { LoginRegisterMode, LoginRegisterPagePresenter } from './login-register-page.presenter'
import { useInjection } from '@core/providers/inversify-react.provider'
import { FeedbackComponent } from '@core/feedback/feedback.component'
import { Button } from '@ui-kit/button.component'
import { TextInput } from '@ui-kit/form.component'

/**
 * Sign in / register, modelled on Tailwind UI's
 * forms/sign-in-forms/simple_card with colours on shell-* tokens. The mode
 * switch is a segmented pair rather than two coloured buttons: they are two
 * states of one control, and colour is not what distinguishes them.
 */
export const LoginRegisterPageComponent = observer(() => {
  const presenter: LoginRegisterPagePresenter = useInjection(LoginRegisterPagePresenter)
  const vm = presenter.viewModel

  const tab = (active: boolean) =>
    `flex-1 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
      active ? 'bg-shell-surface text-shell-ink shadow-xs' : 'text-shell-ink-secondary hover:text-shell-ink'
    }`

  return (
    <div className="flex min-h-full flex-col justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h1 className="text-center text-2xl/9 font-bold tracking-tight text-shell-ink">{vm.heading}</h1>
        <p className="mt-2 text-center text-sm text-shell-ink-secondary">{vm.subheading}</p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="rounded-lg bg-shell-surface px-6 py-8 shadow-sm outline-1 -outline-offset-1 outline-shell-border sm:px-10">
          <div className="mb-6 flex gap-1 rounded-lg bg-shell-surface-muted p-1">
            <button
              type="button"
              className={tab(vm.mode === LoginRegisterMode.LOGIN)}
              onClick={() => presenter.setOption(LoginRegisterMode.LOGIN)}
            >
              {vm.loginLabel}
            </button>
            <button
              type="button"
              className={tab(vm.mode === LoginRegisterMode.REGISTER)}
              onClick={() => presenter.setOption(LoginRegisterMode.REGISTER)}
            >
              {vm.registerLabel}
            </button>
          </div>

          {/* The 'auth' channel's surface. It belongs here rather than in the
              shell's top-right stack: a credentials failure is about this form,
              and the auth scope is configured sticky so it stays while the user
              fixes it. */}
          <div className="mb-4 flex flex-col gap-y-2 empty:mb-0">
            <FeedbackComponent scope="auth.form" />
          </div>

          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault()
              presenter.submit()
            }}
          >
            <TextInput
              name="email"
              type="email"
              autoComplete="username"
              label={vm.emailLabel}
              value={vm.email}
              onChange={presenter.setEmail}
              placeholder={vm.emailPlaceholder}
            />
            <TextInput
              name="password"
              type="password"
              autoComplete={vm.passwordAutocomplete}
              label={vm.passwordLabel}
              value={vm.password}
              onChange={presenter.setPassword}
              placeholder={vm.passwordPlaceholder}
            />
            {vm.showInviteCode && (
              <TextInput
                name="inviteCode"
                type="text"
                autoComplete="off"
                label={vm.inviteCodeLabel}
                value={vm.inviteCode}
                onChange={presenter.setInviteCode}
                placeholder={vm.inviteCodePlaceholder}
              />
            )}
            <Button type="submit" tone="primary" className="w-full">
              {vm.submitLabel}
            </Button>
          </form>

          {/* Always available — guest access is a real product path, not a
              dev-only bypass (unlike the fixture surface gated behind
              `Config.showDemoSurface`). */}
          <div className="mt-6 space-y-4 border-t border-shell-border pt-4">
            <TextInput
              name="guestName"
              type="text"
              autoComplete="off"
              label={vm.guestNameLabel}
              value={vm.guestName}
              onChange={presenter.setGuestName}
            />
            <Button tone="ghost" className="w-full" onClick={() => presenter.enterAsGuest()}>
              {vm.guestLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
})
